import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["id", "description", "footer", "thumbnailSrc", "thumbnailOverlay", "isFlushOnMobile", "disabled", "name", "body", "onClick", "to", "use", "href", "title", "externalLinkProps", "className"];
let _ = t => t,
  _t,
  _t2,
  _t3,
  _t4,
  _t5,
  _t6,
  _t7;
import { useState } from 'react';
import styled from 'styled-components';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UICardSection from 'UIComponents/card/UICardSection';
import { TruncateMultiline } from 'foundations-components/transitional/components/TruncateMultiline';
import HR from 'UIComponents/elements/HR';
import UICardHero from 'UIComponents/card/UICardHero';
import H4 from 'UIComponents/elements/headings/H4';
import { Link } from 'react-router-dom';
import { css, mediaQuery, theme, useMediaQuery } from 'academy-common-lib/utils/Styling';
import UIImage from 'UIComponents/image/UIImage';
import ACSkeletonBox from '../loading/ACSkeletonBox';
import I18n from 'I18n';
import ACDynamicThumbnailImage from './ACDynamicThumbnailImage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LineHeight = '24px';
const linkStyles = css(_t || (_t = _`
  flex-direction: column;
  height: 100%;
  color: inherit;
  font: inherit;
  line-height: inherit;
  text-decoration: inherit;
  display: flex;
  -webkit-font-smoothing: auto;
  :hover {
    color: inherit;
    font: inherit;
    line-height: inherit;
    text-decoration: inherit;
  }
`));
const FlexLink = styled(Link).withConfig({
  displayName: "ACContentCard__FlexLink",
  componentId: "sc-65chhb-0"
})(["", ""], linkStyles);
const ExternalLink = styled.a.withConfig({
  displayName: "ACContentCard__ExternalLink",
  componentId: "sc-65chhb-1"
})(["", ""], linkStyles);
const CardHolder = styled.div.withConfig({
  displayName: "ACContentCard__CardHolder",
  componentId: "sc-65chhb-2"
})(["display:flex;flex-direction:column;height:100%;"]);
const ContentCardLink = ({
  children,
  externalLinkProps,
  to,
  href,
  onClick = () => {}
}) => {
  if (!to && !href) {
    return /*#__PURE__*/_jsx(CardHolder, {
      onClick: onClick,
      children: children
    });
  }
  if (to) {
    return /*#__PURE__*/_jsx(FlexLink, {
      onClick: onClick,
      to: to,
      children: children
    });
  }
  return /*#__PURE__*/_jsx(ExternalLink, Object.assign({}, externalLinkProps, {
    href: href,
    onClick: onClick,
    children: children
  }));
};
const CardWrapper = styled(UICardWrapper).withConfig({
  displayName: "ACContentCard__CardWrapper",
  componentId: "sc-65chhb-3"
})(["box-shadow:none;margin-bottom:0;overflow:hidden;color:", ";background-color:transparent;outline:8px solid transparent;transition:background-color 200ms,outline 200ms;", " &:not(:last-child){", "}"], ({
  $isGhost
}) => $isGhost ? theme.colors.FLINT : 'inherit', ({
  $use
}) => $use !== 'no-hover' && css(_t2 || (_t2 = _`
      &:hover {
        background-color: ${0};
        outline: 8px solid
          ${0};
      }
    `), $use === 'on-light' ? theme.colors.GYPSUM : $use === 'on-surface-raised' ? theme.colors.KOALA // will migrate to trellis
: 'transparent', $use === 'on-light' ? theme.colors.GYPSUM : $use === 'on-surface-raised' ? theme.colors.KOALA : 'transparent'), mediaQuery('mobile', css(_t3 || (_t3 = _`
        margin-right: 32px;
        flex-basis: auto;
      `))));
const CardSection = styled(UICardSection).withConfig({
  displayName: "ACContentCard__CardSection",
  componentId: "sc-65chhb-4"
})(["display:flex;flex-direction:column;padding:20px 0 0;flex:2;", ""], ({
  $isFlushOnMobile
}) => $isFlushOnMobile && mediaQuery('mobile', css(_t4 || (_t4 = _`
        padding: 20px 12px;
      `))));
const CardTitle = styled(H4).withConfig({
  displayName: "ACContentCard__CardTitle",
  componentId: "sc-65chhb-5"
})(["margin-bottom:0;font-weight:500;"]);
const CardBody = styled.div.withConfig({
  displayName: "ACContentCard__CardBody",
  componentId: "sc-65chhb-6"
})(["flex:1 1 ", ";line-height:", ";"], LineHeight, LineHeight);
const CardBodyInner = styled.div.withConfig({
  displayName: "ACContentCard__CardBodyInner",
  componentId: "sc-65chhb-7"
})(["", " ", ""], mediaQuery('notTablet', css(_t5 || (_t5 = _`
      min-height: calc(${0} * 4 + 8px);
    `), LineHeight)), mediaQuery('desktop', css(_t6 || (_t6 = _`
      min-height: calc(${0} * 3 + 8px);
    `), LineHeight)));
const CardFooter = styled.div.withConfig({
  displayName: "ACContentCard__CardFooter",
  componentId: "sc-65chhb-8"
})(["line-height:1.3;"]);
const ColoredHR = styled(HR).withConfig({
  displayName: "ACContentCard__ColoredHR",
  componentId: "sc-65chhb-9"
})(["background-color:", ";"], ({
  $isGhost
}) => $isGhost ? theme.colors.KOALA : theme.colors.GREAT_WHITE);
const ThumbnailOverlayWrapper = styled.div.withConfig({
  displayName: "ACContentCard__ThumbnailOverlayWrapper",
  componentId: "sc-65chhb-10"
})(["position:absolute;height:100%;width:100%;", ""], ({
  disabled
}) => disabled && css(_t7 || (_t7 = _`
      &::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: ${0};
        opacity: 0.6;
        z-index: 1;
        pointer-events: none;
      }
    `), theme.colors.FLINT));
const ACContentCard = _ref => {
  let {
      id,
      description,
      footer,
      thumbnailSrc,
      thumbnailOverlay,
      isFlushOnMobile = true,
      disabled = false,
      name,
      body,
      onClick = () => {},
      to,
      use = 'on-light',
      href,
      title = name,
      externalLinkProps,
      className
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    isMobile
  } = useMediaQuery();
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const thumbnailLoading = thumbnailSrc && !thumbnailLoaded;
  return /*#__PURE__*/_jsx(CardWrapper, Object.assign({
    $isGhost: disabled,
    $use: use,
    "aria-disabled": disabled,
    className: `content-card ${className}`,
    compact: true,
    "data-test-id": "track-card",
    "data-test-is-ghost": disabled,
    style: {
      borderRadius: isMobile ? 0 : undefined
    }
  }, props, {
    children: /*#__PURE__*/_jsxs(ContentCardLink, {
      externalLinkProps: externalLinkProps,
      href: href,
      onClick: onClick,
      to: to,
      children: [/*#__PURE__*/_jsxs(UICardHero, {
        style: {
          position: 'relative',
          borderRadius: 3,
          aspectRatio: '16 / 9',
          overflow: 'hidden'
        },
        children: [/*#__PURE__*/_jsx(ACSkeletonBox, {
          style: {
            position: 'absolute',
            height: '100%',
            width: '100%'
          }
        }), thumbnailSrc ? /*#__PURE__*/_jsx(UIImage, {
          alt: I18n.text('academyCommon.contentCard.thumbnailAlt', {
            title: name
          }),
          loading: "lazy",
          onLoad: () => setThumbnailLoaded(true),
          src: thumbnailSrc,
          style: {
            visibility: thumbnailLoading ? 'hidden' : 'visible',
            position: 'absolute',
            width: '100%'
          }
        }) : /*#__PURE__*/_jsx(ACDynamicThumbnailImage, {
          seed: id,
          style: {
            position: 'absolute',
            width: '100%'
          },
          title: name,
          type: "rectangle"
        }), /*#__PURE__*/_jsx(ThumbnailOverlayWrapper, {
          disabled: disabled,
          children: thumbnailOverlay
        })]
      }), /*#__PURE__*/_jsxs(CardSection, {
        $isFlushOnMobile: isFlushOnMobile,
        flush: true,
        children: [/*#__PURE__*/_jsx(CardTitle, {
          children: title
        }), description && /*#__PURE__*/_jsx(ColoredHR, {
          $isGhost: disabled
        }), (description || body) && /*#__PURE__*/_jsx(CardBody, {
          children: /*#__PURE__*/_jsxs(CardBodyInner, {
            children: [body, typeof description === 'string' ? /*#__PURE__*/_jsx(TruncateMultiline, {
              maxChars: 150,
              text: description || ''
            }) : description]
          })
        }), /*#__PURE__*/_jsx(CardFooter, {
          children: footer
        })]
      })]
    })
  }));
};
export default ACContentCard;